/* global __VERCEL_COMMIT__ */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from "@sentry/react";
import { registerSW } from 'virtual:pwa-register';
import './i18n';
import './index.css';
import App from './App.jsx';
import { BrowserRouter } from 'react-router-dom';
import { initAnalytics } from './lib/analytics';
import { reportPreviousRenderCrash, shouldBootSafeMode } from './lib/renderCrashBeacon';
import { reportAbandonedUploads } from './lib/uploadTelemetry';
import { getRememberedUserId } from './lib/freezeUser';
import installDisableAutofillBar from './lib/disableAutofillBar';
import { buildSentryConfig } from './lib/sentryConfig';
import { watchServiceWorkerUpdates } from './lib/swUpdateCheck';
import { getAdminView } from './lib/adminView';

// Mode sans échec : après 2 crashs renderer consécutifs (boucle iOS « Un
// problème récurrent est survenu »), démarrer 24 h sans PostHog — gros
// consommateur mémoire — pour que l'appareil sorte de la boucle sans
// intervention manuelle. (Le replay Sentry, l'autre gros consommateur, est
// retiré pour tout le monde — voir lib/sentryConfig.js.)
const SAFE_MODE = shouldBootSafeMode();

const SENTRY_CONFIG = buildSentryConfig({
  Sentry,
  env: import.meta.env,
  safeMode: SAFE_MODE,
  release: typeof __VERCEL_COMMIT__ !== 'undefined' && __VERCEL_COMMIT__ ? __VERCEL_COMMIT__ : undefined,
});

// TODO: Re-enable cookie consent before production launch
Sentry.init(SENTRY_CONFIG);

// If a previous load died mid-render (WebKit renderer killed for memory — the
// "problème récurrent" crash that can't self-report), surface it now so it
// shows up attributed in Sentry instead of as "0 users impacted".
// Au démarrage, Sentry ne connaît pas encore l'élève : on reprend le dernier
// id connecté, sinon ces crashs restent anonymes.
const rememberedUserId = getRememberedUserId();
const renderCrashReported = reportPreviousRenderCrash((data) => {
  Sentry.captureMessage('Render crash recovered on next load', {
    level: 'error',
    user: rememberedUserId ? { id: rememberedUserId } : undefined,
    tags: { render_crash: 'true', crash_route: data.route || 'unknown' },
    extra: data,
  });
});

// Page figée (ou tuée) en étant visible au chargement précédent, puis
// surveillance de celui-ci — voir lib/freezeDetector.js. Pas de double
// rapport quand la garde de crash vient de signaler le même chargement.
// Chargé à part : le bundle principal est à sa limite de taille.
import('./lib/freezeDetector')
  .then(({ reportPreviousFreeze, startFreezeDetector }) => {
    reportPreviousFreeze({ skip: renderCrashReported });
    startFreezeDetector();
  })
  .catch(() => {
    // Best-effort : un chunk qui ne charge pas ne doit rien casser.
  });

// Même principe pour les uploads vidéo : une page tuée pendant l'envoi (app
// quittée, écran verrouillé sur Android) ne déclenche aucun onError.
reportAbandonedUploads();

// Product analytics (PostHog). No-ops if VITE_POSTHOG_KEY is absent.
// TODO: gate behind consent before launch (Option B — disclosed in privacy policy).
// En mode sans échec, PostHog n'est pas chargé du tout (poids mémoire).
// En mode admin (lib/adminView.js) non plus : l'équipe Kaiylo dans l'espace
// d'un coach ne doit pas compter comme l'activité de ce coach.
const ADMIN_VIEW = Boolean(getAdminView());
if (ADMIN_VIEW) Sentry.setTag('admin_view', 'true');
if (!SAFE_MODE && !ADMIN_VIEW) {
  initAnalytics();
}

// Pas de barre d'autoremplissage Chrome Android (clé / carte / position)
// au-dessus du clavier sur les champs de l'app — voir disableAutofillBar.js.
installDisableAutofillBar();

registerSW({
  // Onglets ouverts des jours : revérifier sw.js régulièrement (swUpdateCheck.js).
  onRegisteredSW(_swUrl, registration) {
    watchServiceWorkerUpdates(registration);
  },
  onRegisterError(error) {
    console.warn('[PWA] Service worker registration failed', error);
  },
});

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>,
);
